import { useState } from "react";
import { useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, CheckboxField, Modal, PasswordField } from "@fawkes/ui";

/**
 * Admin "set password" dialog — POST /api/users/{id}/password. The alternative to the reset link for a
 * user with no e-mail on file: the admin picks a password and hands it over out-of-band.
 *
 * "Must change at next login" is checked by default, because a password someone else chose should not
 * stay in use. The confirm field is client-side only; the length rule (min 8) is the server's, so a
 * 422 `weak_password` is rendered as its own message rather than assumed away here.
 */
export function SetUserPasswordDialog({
  userId,
  onClose,
  onDone,
}: {
  userId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    if (password !== confirm) {
      setError(t("userDetailView.setPassword.error.mismatch"));
      return;
    }
    mutate(
      { url: `users/${userId}/password`, method: "post", values: { password, mustChange } },
      {
        onSuccess: () => onDone(),
        onError: (err) => {
          const status = (err as { statusCode?: number })?.statusCode;
          setError(
            status === 422
              ? t("userDetailView.setPassword.error.weak")
              : t("userDetailView.setPassword.error.generic"),
          );
        },
      },
    );
  }

  return (
    <Modal
      open
      title={t("userDetailView.setPassword.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={isLoading || !password}>
            {t("userDetailView.setPassword.submit")}
          </Button>
        </>
      }
    >
      <PasswordField
        label={t("userDetailView.setPassword.field.password")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
      />
      <PasswordField
        label={t("userDetailView.setPassword.field.confirm")}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <CheckboxField
        label={t("userDetailView.setPassword.field.mustChange")}
        checked={mustChange}
        onChange={(e) => setMustChange(e.target.checked)}
      />
      <p className="fawkes-muted">{t("userDetailView.setPassword.help")}</p>
      {error && (
        <p className="fawkes-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
